import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ItemDetailsService } from '../services/item-details.service';
import { applyMarginFromItemDetailsToLine } from './item-utils';
import { isMarginApprovalEnabledForDivision } from './margin-approval.utils';

export type MarginLineEnrichmentContext = {
    company: string;
    division: string;
    customerId?: string;
    warehouse?: string;
    defaultPriceList?: string;
};

export function lineNeedsMarginFetch(line: any): boolean {
    // Copy/excel/grid already hydrated from ItemDetails — do not re-hit on Save.
    if (line?.marginDataLoaded === true) {
        return false;
    }
    const cost = Number(line?.cost ?? line?.costPrice) || 0;
    const minMargin = Number(line?.minimumMargin ?? line?.itemMcm) || 0;
    return cost <= 0 || minMargin <= 0;
}

export function linesNeedMarginEnrichment(lines: any[]): boolean {
    return (lines || []).some(lineNeedsMarginFetch);
}

export function enrichLinesMarginData(
    itemDetailsService: ItemDetailsService,
    lines: any[],
    ctx: MarginLineEnrichmentContext
): Observable<any[]> {
    const list = lines || [];
    if (!list.length || !ctx.company || !ctx.division) {
        return of(list);
    }
    if (!isMarginApprovalEnabledForDivision(ctx.division)) {
        return of(list);
    }
    if (!list.some(lineNeedsMarginFetch)) {
        return of(list);
    }

    const requests = list.map((line) => {
        if (!lineNeedsMarginFetch(line)) {
            return of(line);
        }
        const itemNo = String(line?.itemNumber ?? line?.itemNo ?? '').trim();
        if (!itemNo) {
            return of(line);
        }
        return itemDetailsService
            .getItemDetails(
                ctx.company,
                ctx.division,
                {
                    itno: itemNo,
                    cuno: ctx.customerId || '',
                    whlo: ctx.warehouse || ''
                },
                'MARGIN_ENRICH'
            )
            .pipe(
                map((response: any) => {
                    applyMarginFromItemDetailsToLine(
                        line,
                        response,
                        ctx.division,
                        ctx.defaultPriceList || ''
                    );
                    // Even when API returns cost/MCM = 0, do not fetch again on later actions.
                    line.marginDataLoaded = true;
                    return line;
                }),
                catchError(() => {
                    // Failed once — stop retry loops on picklist/invoice enrich.
                    line.marginDataLoaded = true;
                    return of(line);
                })
            );
    });

    return forkJoin(requests);
}
